import {useCallback, useRef} from 'react';

// this hook returns a throttled version of the callback received that runs at most once per delay milliseconds
// it also has an option to run the callback on the leading edge of the delay interval
// and on the trailing edge of the delay interval
// the callback is invoked with the last arguments received
export function useThrottle(
  cb: (...args: any[]) => void,
  delay?: number,
  option = {leading: true, trailing: true},
) {
  const timeoutRef = useRef<NodeJS.Timeout>();
  const lastArgs = useRef<any[]>(); // track the args

  // create a memoized throttle function
  const throttle = useCallback(
    function (...args: any[]) {
      const {trailing, leading} = option;

      const delayFunc = () => {
        // if trailing invoke the function and start the timer again
        if (trailing && lastArgs.current) {
          cb(lastArgs.current);
          lastArgs.current = undefined;
          timeoutRef.current = setTimeout(delayFunc, delay);
        } else {
          // else reset the timer
          timeoutRef.current = undefined;
        }
      };

      // if leading run it right away
      if (!timeoutRef.current && leading) {
        cb(args);
      }
      // else store the args
      else {
        lastArgs.current = args;
      }

      // run the delayed call
      if (!timeoutRef.current) {
        timeoutRef.current = setTimeout(delayFunc, delay);
      }
    },
    [cb, delay, option],
  );

  return throttle;
}
